import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "nammaeventz-169f1",
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:475302109478:web:9fc33639bc8ba5c92e21e7"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

let messaging = null;

// 🔍 Helper: Check if backend server is reachable before FCM
export const isBackendAvailable = async () => {
    if (process.env.NODE_ENV === 'production') {
        // In production, assume backend is available
        return true;
    }

    // In development, check if server is actually running
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) return false;

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        
        const response = await fetch(apiUrl + '/health', {
            method: 'GET',
            signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        return response.ok;
    } catch (_) {
        console.log('ℹ️ Backend not reachable, FCM will not be initialized');
        return false;
    }
};

// Initialize messaging conditionally
export const initMessaging = async () => {
    try {
        // 🔴 CRITICAL: Don't initialize FCM if backend is offline
        const backendAvailable = await isBackendAvailable();
        if (!backendAvailable) {
            console.log('ℹ️ Backend unavailable - FCM initialization skipped');
            return null;
        }

        const supported = await isSupported();
        if (supported) {
            messaging = getMessaging(app);
            console.log('✅ Firebase Messaging initialized');
            return messaging;
        } else {
            console.log('ℹ️ Firebase Messaging not supported in this browser');
            return null;
        }
    } catch (err) {
        console.error('⚠️ Error checking messaging support:', err);
        return null;
    }
};

// Helper to retrieve token (VAPID key required)
export const requestFcmToken = async (vapidKey) => {
    try {
        // 🔴 CRITICAL: Verify backend is available before requesting FCM token
        const backendAvailable = await isBackendAvailable();
        if (!backendAvailable) {
            console.log('ℹ️ Backend unavailable - FCM token request skipped');
            return null;
        }

        if (!messaging) {
            await initMessaging();
        }

        if (!messaging) {
            console.log('ℹ️ Firebase Messaging not available');
            return null;
        }

        const currentToken = await getToken(messaging, {
            vapidKey: vapidKey
        });

        if (currentToken) {
            console.log('✅ FCM Token generated:', currentToken.substring(0, 20) + '...');
            return currentToken;
        } else {
            console.log('ℹ️ No registration token available. User may have denied permission.');
            return null;
        }
    } catch (err) {
        console.warn('⚠️ Error retrieving FCM token:', err.message);
        return null;
    }
};

// Helper for listening to foreground messages
export const onMessageListener = () =>
    new Promise((resolve) => {
        initMessaging().then((msg) => {
            if (msg) {
                onMessage(msg, (payload) => {
                    resolve(payload);
                });
            } else {
                resolve(null);
            }
        });
    });

// Helper for listening to token refresh
// In Firebase v9+, the browser handles refresh automatically
export const onTokenRefreshListener = (callback) => {
    // Placeholder for custom refresh logic if needed
};

export { messaging };
